package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Assistant_conversation
import com.tedd.db.Assistant_message
import com.tedd.db.SelectLastAnswers
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Les conversations avec l'assistant d'une compétence. L'appareil crée, renomme,
 * supprime et restaure une conversation ; il n'écrit jamais un message : ils arrivent
 * du serveur par la synchronisation.
 */
class AssistantRepository(private val db: TeddDatabase) {

    fun observeConversations(skillId: String): Flow<List<Assistant_conversation>> =
        db.assistantConversationQueries.selectBySkill(skillId).asFlow().mapToList(Dispatchers.Default)

    /** Supprimées depuis moins de 30 jours. */
    fun observeTrash(skillId: String, now: Long = nowMillis()): Flow<List<Assistant_conversation>> =
        db.assistantConversationQueries.selectTrashBySkill(skillId, now - TRASH_MS).asFlow().mapToList(Dispatchers.Default)

    fun observeMessages(conversationId: String): Flow<List<Assistant_message>> =
        db.assistantMessageQueries.selectByConversation(conversationId).asFlow().mapToList(Dispatchers.Default)

    /** La dernière réponse de chaque conversation de la compétence, pour la liste. */
    fun observeLastAnswers(skillId: String): Flow<List<SelectLastAnswers>> =
        db.assistantMessageQueries.selectLastAnswers(skillId).asFlow().mapToList(Dispatchers.Default)

    /** Ouvre une conversation, sans titre. Null si la compétence en a déjà [MAX_PER_SKILL]. */
    @OptIn(ExperimentalUuidApi::class)
    suspend fun create(skillId: String): String? = withContext(Dispatchers.Default) {
        if (db.assistantConversationQueries.countLive(skillId).executeAsOne() >= MAX_PER_SKILL) return@withContext null
        val id = Uuid.random().toString()
        val now = nowMillis()
        db.assistantConversationQueries.insert(id, skillId, null, now, now)
        id
    }

    /** Renomme ; rend le problème, ou null. Un titre vide redonne la main au serveur. */
    suspend fun rename(id: String, title: String): String? = withContext(Dispatchers.Default) {
        val clean = title.trim()
        if (clean.length > TITLE_MAX) return@withContext "Le titre fait au plus $TITLE_MAX caractères."
        if (clean.isEmpty()) return@withContext "Donne un titre à la conversation."
        db.assistantConversationQueries.rename(clean, nowMillis(), id)
        null
    }

    /** Supprime : le serveur fait suivre ses messages. */
    suspend fun delete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.assistantConversationQueries.softDelete(now, now, id)
    }

    /** Sort de la corbeille ; null, ou le problème (limite atteinte). */
    suspend fun restore(id: String): String? = withContext(Dispatchers.Default) {
        val row = db.assistantConversationQueries.selectByIdAnyState(id).executeAsOneOrNull() ?: return@withContext null
        if (db.assistantConversationQueries.countLive(row.skill_id).executeAsOne() >= MAX_PER_SKILL) {
            return@withContext "La compétence a déjà $MAX_PER_SKILL conversations. Supprimes-en une d'abord."
        }
        db.assistantConversationQueries.restoreFromTrash(nowMillis(), id)
        null
    }

    suspend fun get(id: String): Assistant_conversation? = withContext(Dispatchers.Default) {
        db.assistantConversationQueries.selectByIdAnyState(id).executeAsOneOrNull()
    }

    /** La ressource d'une source, si elle existe encore sur l'appareil. */
    suspend fun resource(id: String): com.tedd.db.Skill_resource? = withContext(Dispatchers.Default) {
        db.skillResourceQueries.selectById(id.lowercase()).executeAsOneOrNull()
            ?: db.skillResourceQueries.selectById(id).executeAsOneOrNull()
    }

    companion object {
        const val MAX_PER_SKILL = 200
        const val TITLE_MAX = 200
        private const val TRASH_MS = 30L * 24 * 3600 * 1000
    }
}
